import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CalendarDays, CalendarOff, ClipboardList, Clock, Plus, RefreshCw, Search, Users } from 'lucide-react';
import { attendanceAPI } from '../../services/api';
import { useToast } from '../UIProvider';
import { clinicToday, formatClinicTime } from '../../utils/clinicTime';
import { onActivateKey, ROW_FOCUS_RING } from '../../utils/keyboard';
import EntryDetailModal from './EntryDetailModal';
import { ManualEntryModal } from './EntryEditModals';
import { REVIEW_LABEL, STATUS_LEGEND, datePart, dayFlags, formatDistance, formatHours, roleLabel, statusOf } from './attendanceStatus';
import LeaveTab from './LeaveTab';
import RegisterGrid, { MonthPicker, monthKey } from './RegisterGrid';
import TablePager, { usePagedRows } from '../TablePager';

// Everyone's attendance, for whoever runs the hospital: who is in today, the month's register,
// the punches made outside the hospital that need a decision, and the days the system closed
// because nobody punched out. Each row opens the entry with its selfies.

const time = (value) => formatClinicTime(value, { hour: 'numeric', minute: '2-digit', hour12: true }, '');

const Loading = ({ what }) => (
  <div className="bg-white rounded-2xl shadow-xl p-10 text-center">
    <RefreshCw className="h-6 w-6 text-emerald-500 animate-spin mx-auto mb-2" />
    <p className="text-sm text-gray-500">Loading {what}…</p>
  </div>
);

const Empty = ({ children }) => (
  <div className="bg-white rounded-2xl shadow-xl p-10 text-center text-sm text-gray-500">{children}</div>
);

// ---------------------------------------------------------------- Today

const TodayTab = ({ onOpen, reloadKey }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await attendanceAPI.today();
      setRows(Array.isArray(res) ? res : []);
      setError(null);
    } catch (e) {
      setError(e?.message || "Today's attendance could not be loaded.");
    }
  }, []);
  useEffect(() => { load(); }, [load, reloadKey]);

  const stats = useMemo(() => {
    const list = rows || [];
    const by = (f) => list.filter(f).length;
    return {
      in: by((r) => r.day?.open),
      present: by((r) => ['PRESENT', 'HALF_DAY'].includes(r.day?.status)),
      late: by((r) => r.day?.late),
      notIn: by((r) => r.day?.status === 'NOT_IN'),
      absent: by((r) => r.day?.status === 'ABSENT'),
      flagged: by((r) => r.day?.flagged),
    };
  }, [rows]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rows || []).filter((r) => !q || (r.userName || '').toLowerCase().includes(q) || roleLabel(r.role).toLowerCase().includes(q));
  }, [rows, query]);
  const paging = usePagedRows(shown, { id: 'attendance-today', resetOn: [query] });

  if (error) return <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-sm text-red-800">{error}</div>;
  if (!rows) return <Loading what="today" />;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          ['In now', stats.in, 'text-navy-800'],
          ['Present', stats.present, 'text-emerald-700'],
          ['Late', stats.late, 'text-amber-700'],
          ['Not in yet', stats.notIn, 'text-gray-600'],
          ['Absent', stats.absent, 'text-red-700'],
          ['To review', stats.flagged, 'text-amber-700'],
        ].map(([label, value, tone]) => (
          <p key={label} className="bg-white rounded-xl shadow-md p-4 text-xs text-gray-500">
            {label}
            <span className={`block text-2xl font-bold ${tone}`}>{value}</span>
          </p>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-xl">
        <div className="p-4 border-b border-gray-100">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or role"
              aria-label="Search staff"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>
        {/* Square corners: the bar sits under the search box, not at the card's top edge. */}
        <TablePager paging={paging} noun="member of staff" nounPlural="members of staff" filteredFrom={(rows || []).length} className="px-4" rounded={false} />
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col />
            <col className="w-28" />
            <col className="w-24" />
            <col className="w-24" />
            <col className="w-20" />
            <col className="w-40" />
          </colgroup>
          <thead>
            <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">In</th>
              <th className="px-4 py-3">Out</th>
              <th className="px-4 py-3 text-right">Hours</th>
              <th className="px-4 py-3">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {shown.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Nobody matches.</td></tr>
            )}
            {paging.pageRows.map((r) => {
              const st = statusOf(r.day);
              const open = r.entry ? () => onOpen(r.entry) : null;
              return (
                <tr
                  key={r.userId}
                  tabIndex={open ? 0 : undefined}
                  onClick={open || undefined}
                  onKeyDown={open ? onActivateKey(open) : undefined}
                  className={`${open ? `cursor-pointer hover:bg-gray-50 ${ROW_FOCUS_RING}` : ''}`}
                >
                  <td className="px-4 py-3 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{r.userName}</div>
                    <div className="text-xs text-gray-500">{roleLabel(r.role)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${st.chip}`}>
                      {r.day?.open ? 'In' : st.label || '–'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{time(r.day?.clockInAt) || '–'}</td>
                  <td className="px-4 py-3 text-gray-700">{r.day?.open ? '–' : time(r.day?.clockOutAt) || '–'}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{r.day?.hoursWorked > 0 ? formatHours(r.day.hoursWorked) : '–'}</td>
                  <td className="px-4 py-3 text-xs">
                    {r.day?.late && <span className="text-amber-700 block">Late {r.day.lateMinutes} min</span>}
                    {/* Somebody started this person's record for them. Allowed and recorded, so it
                        reads as a note rather than as something waiting to be decided. */}
                    {[r.entry?.in?.markedByName, r.entry?.out?.markedByName].filter(Boolean).map((who, i) => (
                      <span key={`${who}-${i}`} className="text-gray-500 block">Marked by {who}</span>
                    ))}
                    {dayFlags(r.day).map((f) => (
                      <span key={f.key} className={`block ${f.tone === 'amber' ? 'text-amber-700' : f.tone === 'red' ? 'text-red-700' : 'text-gray-500'}`}>{f.label}</span>
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <TablePager paging={paging} noun="member of staff" nounPlural="members of staff" filteredFrom={(rows || []).length} position="bottom" className="px-4" />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- Register

const RegisterTab = ({ onOpenDay, reloadKey }) => {
  const [month, setMonth] = useState(monthKey(clinicToday()));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await attendanceAPI.register(month);
      setData(res);
      setError(null);
    } catch (e) {
      setError(e?.message || 'The register could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [month]);
  useEffect(() => { load(); }, [load, reloadKey]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl shadow-xl p-4 flex items-center justify-between gap-3 flex-wrap">
        <MonthPicker month={month} onMonth={setMonth} max={monthKey(clinicToday())} />
        <p className="text-xs text-gray-500">{STATUS_LEGEND} · a dot marks something to look at</p>
      </div>

      {error ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-sm text-red-800">{error}</div>
      ) : loading || !data ? (
        <Loading what="the register" />
      ) : (
        <RegisterGrid data={data} onOpenDay={onOpenDay} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------- Flagged and corrections

const EntryTable = ({ entries, onOpen, columns, pagerId, noun, nounPlural }) => {
  const paging = usePagedRows(entries, { id: pagerId });
  return (
    <div className="bg-white rounded-2xl shadow-xl">
      <TablePager paging={paging} noun={noun} nounPlural={nounPlural} className="px-4" />
      <table className="w-full table-fixed text-sm">
        <colgroup>
          <col />
          <col className="w-36" />
          <col className="w-24" />
          <col className="w-24" />
          <col className="w-44" />
        </colgroup>
        <thead>
          <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <th className="px-4 py-3">Name</th>
            <th className="px-4 py-3">Day</th>
            <th className="px-4 py-3">In</th>
            <th className="px-4 py-3">Out</th>
            <th className="px-4 py-3">{columns.last}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {paging.pageRows.map((e) => {
            const open = () => onOpen(e);
            return (
              <tr key={e.id} tabIndex={0} onClick={open} onKeyDown={onActivateKey(open)} className={`cursor-pointer hover:bg-gray-50 ${ROW_FOCUS_RING}`}>
                <td className="px-4 py-3 font-medium text-gray-900 truncate">{e.userName}</td>
                <td className="px-4 py-3 text-gray-700">{datePart(e.workDate, { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                <td className="px-4 py-3 text-gray-700">{time(e.clockInAt) || '–'}</td>
                <td className="px-4 py-3 text-gray-700">{e.status === 'OPEN' ? '–' : time(e.clockOutAt) || '–'}</td>
                <td className="px-4 py-3 text-xs text-gray-700">{columns.render(e)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <TablePager paging={paging} noun={noun} nounPlural={nounPlural} position="bottom" className="px-4" />
    </div>
  );
};

const whereLine = (e) => {
  const sides = [['In', e.in], ['Out', e.out]].filter(([, s]) => s && (s.withinRange === false || s.latitude == null));
  if (sides.length === 0) return 'At hospital';
  const where = (s) => {
    if (s.latitude == null) return 'no position';
    return s.approximate ? 'approximate location' : `${formatDistance(s.distanceMeters)} away`;
  };
  return sides.map(([label, s]) => `${label}: ${where(s)}`).join(' · ');
};

const FlaggedTab = ({ onOpen, reloadKey }) => {
  const [all, setAll] = useState(false);
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    setEntries(null);
    attendanceAPI.flagged(all)
      .then((rows) => { if (live) { setEntries(Array.isArray(rows) ? rows : []); setError(null); } })
      .catch((e) => { if (live) setError(e?.message || 'The flagged punches could not be loaded.'); });
    return () => { live = false; };
  }, [all, reloadKey]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2" role="group" aria-label="Which punches">
        {[[false, 'To review'], [true, 'Reviewed too']].map(([v, label]) => (
          <button key={label} type="button" aria-pressed={all === v} onClick={() => setAll(v)} className={`px-4 py-2 rounded-lg text-sm font-medium ${all === v ? 'bg-navy-800 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}>
            {label}
          </button>
        ))}
      </div>
      {error ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-sm text-red-800">{error}</div>
      ) : !entries ? (
        <Loading what="flagged punches" />
      ) : entries.length === 0 ? (
        <Empty>{all ? 'No punch has been flagged.' : 'Nothing to review. Every punch was made at the hospital.'}</Empty>
      ) : (
        <EntryTable
          entries={entries}
          onOpen={onOpen}
          pagerId="attendance-flagged"
          noun="punch"
          nounPlural="punches"
          columns={{
            last: 'Where',
            render: (e) => (
              <>
                <span className="block">{whereLine(e)}</span>
                {e.reviewStatus !== 'FLAGGED' && <span className="block text-gray-500">{REVIEW_LABEL[e.reviewStatus]} by {e.reviewedByName}</span>}
              </>
            ),
          }}
        />
      )}
    </div>
  );
};

const CorrectionsTab = ({ onOpen, reloadKey }) => {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    attendanceAPI.correctionsDue()
      .then((rows) => { if (live) { setEntries(Array.isArray(rows) ? rows : []); setError(null); } })
      .catch((e) => { if (live) setError(e?.message || 'The list could not be loaded.'); });
    return () => { live = false; };
  }, [reloadKey]);

  if (error) return <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-sm text-red-800">{error}</div>;
  if (!entries) return <Loading what="corrections" />;
  if (entries.length === 0) return <Empty>Nothing to correct. Every shift was punched out.</Empty>;
  return (
    <EntryTable
      entries={entries}
      onOpen={onOpen}
      pagerId="attendance-corrections"
      noun="shift"
      columns={{ last: 'Why', render: () => 'No punch-out; closed by the system' }}
    />
  );
};

// ---------------------------------------------------------------- The page

const AttendanceBoard = ({ hasPermission, defaultTab }) => {
  const notify = useToast();
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('ATTENDANCE', action) : true),
    [hasPermission],
  );
  const canReview = can('REVIEW_FLAGGED');
  const canCorrect = can('CORRECT_PUNCH');
  const canApproveLeave = can('APPROVE_LEAVE');
  const canSeeLeave = canApproveLeave || can('VIEW_ALL');

  const tabs = useMemo(() => [
    { key: 'today', label: 'Today', icon: Users, allowed: true },
    { key: 'register', label: 'Register', icon: CalendarDays, allowed: true },
    { key: 'leave', label: 'Leave', icon: CalendarOff, allowed: canSeeLeave },
    { key: 'flagged', label: 'To review', icon: AlertTriangle, allowed: canReview },
    { key: 'corrections', label: 'Corrections', icon: ClipboardList, allowed: canCorrect },
  ].filter((t) => t.allowed), [canReview, canCorrect, canSeeLeave]);

  const [tab, setTab] = useState(() => (tabs.some((t) => t.key === defaultTab) ? defaultTab : 'today'));
  const [selected, setSelected] = useState(null); // an Entry open in the detail modal
  const [adding, setAdding] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((n) => n + 1);

  // A register cell knows the day, not the entry: fetch it, then open it.
  const openDay = async (userId, date) => {
    try {
      const rows = await attendanceAPI.entries({ from: date, to: date, userId });
      const entry = (Array.isArray(rows) ? rows : []).find((e) => e.reviewStatus !== 'REJECTED') || rows?.[0];
      if (entry) setSelected(entry);
      else notify('info', 'No entry that day.');
    } catch (e) {
      notify('error', e?.message || 'The entry could not be opened.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
                <Clock className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Attendance</h1>
                <p className="text-gray-600">Who is in, the month's register, and the punches that need a look</p>
              </div>
            </div>
            {canCorrect && (
              <button type="button" onClick={() => setAdding(true)} className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium">
                <Plus className="h-4 w-4" aria-hidden="true" /> Add entry by hand
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap" role="group" aria-label="Attendance views">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                aria-pressed={active}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-navy-800 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'today' && <TodayTab onOpen={setSelected} reloadKey={reloadKey} />}
        {tab === 'register' && <RegisterTab onOpenDay={openDay} reloadKey={reloadKey} />}
        {tab === 'leave' && canSeeLeave && <LeaveTab canDecide={canApproveLeave} reloadKey={reloadKey} />}
        {tab === 'flagged' && canReview && <FlaggedTab onOpen={setSelected} reloadKey={reloadKey} />}
        {tab === 'corrections' && canCorrect && <CorrectionsTab onOpen={setSelected} reloadKey={reloadKey} />}
      </div>

      {selected && (
        <EntryDetailModal
          entry={selected}
          canReview={canReview}
          canCorrect={canCorrect}
          onClose={() => setSelected(null)}
          onChanged={() => reload()}
        />
      )}
      {adding && (
        <ManualEntryModal onClose={() => setAdding(false)} onSaved={() => { setAdding(false); reload(); }} />
      )}
    </div>
  );
};

export default AttendanceBoard;
